import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardText,
} from 'reactstrap';

import { CardHeaderWithToolbars, Title } from '../../../components/Card/CardHeaderWithToolbars';
import SalesCursorPagination from '../../../components/Paginations/SalesCursorPagination';
import Toolbars from './Components/Toolbars';
import FiltersPanel from '../Filters';
import FilterContainer from '../../../components/Container/FilterContainer/FilterContainer';
import { Tbl, TblHead, TblBody } from '../../../components/Tbl';
import ModalDeliver from '../Delivering/ModalSubmitCourier';
import { ModalBulkUpload, ModalInfoCreditCard } from '../../../components/Modals';
import { DEFAULT_SORT_BY, DEFAULT_PER_PAGE } from '../../../constants/settings';
import { getSaleList, doDeliverSneakerSale, doPrintSaleLabels, doConfirmMarketplacePickup, submitSaleReturn } from '../../../actions/saleAction';
import privateView from '../../../components/hocs/privateView';
import RowItem from '../Delivering/RowItem';
import RowHeader from '../Delivering/RowHeader';
import ModalPurchasePrice from '../ModalPurchasePrice';
import ModalFollowUp from '../ModalFollowUp';
import Analytics from '../../../services/Analytics';
import { bulkAWBFormats } from '../../../config';
import { doBulkAWB } from '../../../actions/bulkUploadAwbAction';
import { bulkUploadAWBErrorMessageSelector, bulkUploadAWBIsSubmitSelector, bulkUploadAWBSuccessSelector } from '../../../reducers/selectors/bulkUploadAWBSelector';
import PrintSelectionBar from './Components/PrintSelectionBar';
import ModalMarketplacePickup from './Components/ModalMarketplacePickup';
import ModalRelistListing from './Components/ModalRelistListing';
import { isMarketplacePickupSale, resolveExternalOrderId, resolveMarketplaceLogistics } from './Helper/MarketplacePickupHelper';
import { returnErrorMessage } from '../../Returns/returnSale';

const defaultParams = {
  type: 'verification_passed',
  sort_by: DEFAULT_SORT_BY,
  per_page: DEFAULT_PER_PAGE,
};

const PreDeliveringContainer = ({
  createdSource,
  title,
  analyticsPageName,
  showCreatedSourceFilter,
  createdSourceOptions,
  ...props
}) => {
  const getInitialParams = () => (
    createdSource ? { ...defaultParams, created_source: createdSource } : { ...defaultParams }
  );

  const [params, setParams] = useState(getInitialParams);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalDeliver, setModalDeliver] = useState(false);
  const [formDeliverSelected, setFormDeliverSelected] = useState(null);
  const [deliverProcess, setDeliverProcess] = useState(false);
  const [modalCreditCard, setModalCreditCard] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState(null);
  const [dataModalPurchasePrice, setDataModalPurchasePrice] = useState(null);
  const [isModalPurchasePrice, setIsModalPurchasePrice] = useState(false);
  const [dataModalFollowUp, setDataModalFollowUp] = useState(null);
  const [isModalFollowUp, setIsModalFollowUp] = useState(false);
  const [modalBulkUpload, setModalBulkUpload] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isResetFilter, setIsResetFilter] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const [printOpen, setPrintOpen] = useState(false);
  const [modalMarketplacePickup, setModalMarketplacePickup] = useState(false);
  const [relistSale, setRelistSale] = useState(null);
  const [relistError, setRelistError] = useState(null);
  const [relistSubmitting, setRelistSubmitting] = useState(false);

  const paramsRef = useRef(params);
  const prevIsSubmitRef = useRef(props.sale.isSubmit);
  const deliverProcessRef = useRef(false);
  useEffect(() => { paramsRef.current = params; });

  const { getList } = props;
  const rows = Array.isArray(props.sale.list.data) ? props.sale.list.data : [];
  const selectedIds = useMemo(() => new Set(selectedData.map(item => item.id)), [selectedData]);

  const _getList = useCallback((rawParams = {}) => {
    let p = { ...rawParams };
    if (p.voucher) {
      const { voucher, ...other } = p;
      if (Number(voucher.id)) {
        p = { ...other, voucher_id: voucher.id };
      } else {
        p = { ...other, voucher_code: voucher.code };
      }
    }
    if (p.seller && p.seller.id) {
      const { seller, ...other } = p;
      p = { ...other, seller_id: seller.id };
    }
    const cleanParams = Object.keys(p)
      .filter(key => p[key] !== '')
      .reduce((obj, current) => ({ ...obj, [current]: p[current] }), {});
    return getList({ params: cleanParams });
  }, [getList]);

  const _refreshList = useCallback(() => {
    const nextParams = { ...paramsRef.current };
    setSelectedData([]);
    setParams(nextParams);
    _getList(nextParams);
  }, [_getList]);

  const _toggleFilterPanel = () => setFilterOpen(prev => !prev);

  const _handleResetFilter = () => {
    const nextParams = getInitialParams();
    setSelectedData([]);
    setParams(nextParams);
    setIsResetFilter(true);
    _getList(nextParams);
  };

  const _handleApplyFilter = (payloadFilter) => {
    const nextParams = { ...paramsRef.current, ...payloadFilter };
    setSelectedData([]);
    setParams(nextParams);
    _getList(nextParams);
  };

  const _handleCategoryChanged = (category) => {
    const nextParams = {
      ...paramsRef.current,
      category: category === '' ? category : category.value,
    };
    setSelectedData([]);
    setParams(nextParams);
    _getList(nextParams);
  };

  const _onOpenCreditCardModal = (item) => {
    setInvoiceNumber(item.invoice_number);
    setModalCreditCard(prev => !prev);
  };

  const _togglePurchasePriceModal = (value, payloads = null) => {
    setIsModalPurchasePrice(value);
    setDataModalPurchasePrice(payloads ? { ...payloads } : null);
  };

  const _toggleFollowUpModal = (value, payloads = null) => {
    setIsModalFollowUp(value);
    setDataModalFollowUp(payloads ? { ...payloads } : null);
  };

  const _onOpenDeliverModal = (item) => {
    // TikTok and Shopee confirm through the read-only pickup modal. The row
    // button is disabled until an AWB exists; this refuses the same click if
    // it still arrives.
    if (isMarketplacePickupSale(item) && !resolveMarketplaceLogistics(item).hasAwb) {
      return;
    }
    setFormDeliverSelected(item);
    setSelectedRow(item.id);
    if (isMarketplacePickupSale(item)) {
      setModalMarketplacePickup(true);
      return;
    }
    setModalDeliver(prev => !prev);
  };

  const _onCloseDeliverModal = () => {
    setFormDeliverSelected(null);
    setSelectedRow(null);
    setModalDeliver(prev => !prev);
  };

  const _onCloseMarketplacePickupModal = () => {
    setFormDeliverSelected(null);
    setSelectedRow(null);
    setModalMarketplacePickup(false);
  };

  // The sale leaves VERIFICATION_PASSED on a successful confirm, so it is gone
  // from this list — refetch rather than dropping it from local state, which
  // would leave the paginator counting a row the server no longer returns.
  //
  // It must also leave the print selection. `print-labels` rejects the WHOLE
  // batch if any id is no longer verification_passed, so one confirmed row left
  // ticked would silently block every later print until the page is reloaded —
  // and the operator would have no way to tell which row caused it.
  //
  // Analytics deliberately records nothing here: recordDeliverOrder describes a
  // courier handover and its payload is built around `notes`, which this flow
  // has no field for. A marketplace pickup is a different event and would need
  // its own, rather than a courier event with empty arguments.
  const _onOpenRelist = (item) => {
    setRelistError(null);
    setRelistSale(item);
  };

  const _onCloseRelist = () => {
    if (relistSubmitting) return;
    setRelistSale(null);
    setRelistError(null);
  };

  const _onConfirmRelist = async () => {
    if (relistSubmitting || !relistSale) return;
    setRelistError(null);
    setRelistSubmitting(true);
    try {
      const result = await props.restoreSellerStock(relistSale.id);
      if (!result || !result.ok) {
        setRelistError(returnErrorMessage(result && result.error));
        return;
      }
      const restoredId = relistSale.id;
      setRelistSale(null);
      setSelectedData(prev => prev.filter(el => el.id !== restoredId));
      _refreshList();
    } catch (err) {
      console.error(err);
      setRelistError(returnErrorMessage(err));
    } finally {
      setRelistSubmitting(false);
    }
  };

  const _onMarketplacePickupConfirmed = (confirmedId) => {
    setSelectedData(prev => prev.filter(el => el.id !== confirmedId));
    setFormDeliverSelected(null);
    setSelectedRow(null);
    setModalMarketplacePickup(false);
    // Deliberately _refreshList (page 1) rather than the courier path's
    // _getList(paramsRef.current): the confirmed sale has left this list, so the
    // operator's page may no longer hold the rows it did. Consistent with
    // onPrinted, which resets for the same reason.
    _refreshList();
  };

  const _onDelivery = (id, data) => {
    const { email } = props;
    const selected = formDeliverSelected;
    deliverProcessRef.current = true;
    setDeliverProcess(true);
    props.deliverSneaker(id, data);
    Analytics.recordDeliverOrder(
      email,
      data.notes,
      selected && selected.invoice_number,
      selected && selected.buyer_email,
      selected && selected.price
    );
  };

  // Watch the deliver-submit lifecycle: on completion close the modal and refresh.
  useEffect(() => {
    const prev = prevIsSubmitRef.current;
    if (deliverProcessRef.current && prev && props.sale.isSubmit !== prev) {
      if (props.sale.detail.status_code === 200) {
        setDeliverProcess(false);
        setModalDeliver(false);
        setSelectedRow(null);
        _refreshList();
      } else {
        setDeliverProcess(false);
      }
      deliverProcessRef.current = false;
    }
    prevIsSubmitRef.current = props.sale.isSubmit;
  }, [props.sale.isSubmit, props.sale.detail, _refreshList]);

  const _handleOnSubmitBulkUpload = (payload) => {
    props.doBulkAWB(payload);
  };

  const onSelectDataFromCheckbox = (param) => {
    setSelectedData((prevSelected) => {
      if (prevSelected.length === 0) return [param];
      const isAlreadySelected = prevSelected.some(el => el.id === param.id);
      if (isAlreadySelected) return prevSelected.filter(el => el.id !== param.id);
      return [...prevSelected, param];
    });
  };

  const onPrinted = () => {
    _refreshList();
  };

  useEffect(() => {
    const { email } = props.injectedProps;
    _getList(paramsRef.current).then(() => {
      setIsDataLoaded(true);
    });
    Analytics.recordPageView(analyticsPageName || 'delivering_sales', email, {
      url: window.location.href,
      location: props.location,
      match: props.match,
    });
  }, []);

  const _onRenderRowItem = () => {
    if (props.sale.list.status_code === 200 && rows.length > 0) {
      return rows.map((item, index) => {
        const rowId = index + 1;
        return (
          <RowItem
            key={item.id}
            rowId={rowId}
            data={item}
            id={item.id}
            quantity={item.offer.quantity}
            buyerEmail={item.buyer_email}
            invoiceNumber={item.invoice_number}
            orderDate={item.created_at}
            updatedAt={item.updated_at}
            totalAmount={item.total_price}
            displayName={item.sale_product.display_name}
            SKU={item.sale_product.SKU}
            colour={item.sale_product.colour}
            preOrder={item.sale_product.pre_order}
            preVerified={item.sale_product.pre_verified}
            size={item.sale_product.size.US}
            isSelected={selectedRow === item.id}
            hijacked={item.hijacked}
            status={item.status}
            autoDisburse={item.autodisburse}
            onOpenDeliverModal={() => _onOpenDeliverModal(item)}
            onOpenRelist={() => _onOpenRelist(item)}
            marketplacePickup={isMarketplacePickupSale(item)}
            externalOrderId={resolveExternalOrderId(item)}
            consignmentId={item.sale_product.consignment_id}
            sneakersCondition={item.sale_product.sneakers_condition}
            category={item.sale_product.category_name}
            bank={item.offer.bank_code}
            paymentVia={item.offer.payment_via}
            paymentMethod={item.offer.payment_method}
            onOpenCreditCard={() => _onOpenCreditCardModal(item)}
            marked={item.marked}
            notes={item.sale_notes}
            loginId={props.injectedProps.id}
            legitCheckLogs={item.legit_check_logs}
            markedAt={item.open_marked_at}
            markedBy={item.open_marked_by}
            closeMarkedAt={item.close_marked_at}
            unreadNoteCount={item.unread_notes_count}
            rack={item.offer.user_sell.rack}
            purchasePrice={item.purchase_price}
            compensation={item.compensation_amount}
            followUpStatus={item.follow_up_status}
            sellerEmail={item.offer.user_sell.user?.email}
            voucherCode={item.voucher_code ? item.voucher_code : item.voucherable ? item.voucherable.code : item.voucherable_id}
            openPurchasePriceModal={(payloads) => _togglePurchasePriceModal(true, payloads)}
            openFollowUpModal={(payloads) => _toggleFollowUpModal(true, payloads)}
            isBuyerFirstOrder={item?.is_buyer_first_order}
            isChecked={selectedIds.has(item.id)}
            isOpenPrint={printOpen}
            onSelectDataFromCheckbox={() => onSelectDataFromCheckbox(item)}
          />
        );
      });
    }
    return null;
  };

  const _handleLoadMore = (cursor) => {
    if (!cursor || props.sale.isFetch) return;
    _getList({ ...paramsRef.current, cursor });
  };

  const _onRenderPagination = () => (
    <SalesCursorPagination
      loadedCount={rows.length}
      hasMore={props.sale.list.has_more}
      nextCursor={props.sale.list.next_cursor}
      isLoading={props.sale.isLoadingMore}
      isFetching={props.sale.isFetch}
      error={props.sale.listError}
      onLoadMore={_handleLoadMore}
      onRetry={_refreshList}
    />
  );

  return (
    <Row>
      <ModalFollowUp
        data={dataModalFollowUp}
        isOpen={isModalFollowUp}
        toggle={() => _toggleFollowUpModal(false)}
      />
      <ModalPurchasePrice
        data={dataModalPurchasePrice}
        isOpen={isModalPurchasePrice}
        toggle={() => _togglePurchasePriceModal(false)}
      />
      <ModalDeliver
        isOpen={modalDeliver}
        onCancel={_onCloseDeliverModal}
        onSubmit={(id, form) => _onDelivery(id, form)}
        sale={formDeliverSelected}
        error={props.sale.error}
        isSubmit={deliverProcess}
      />
      <ModalMarketplacePickup
        isOpen={modalMarketplacePickup}
        sale={formDeliverSelected}
        onCancel={_onCloseMarketplacePickupModal}
        onConfirm={props.confirmMarketplacePickup}
        onConfirmed={_onMarketplacePickupConfirmed}
      />
      <ModalRelistListing
        isOpen={relistSale != null}
        sale={relistSale}
        isSubmitting={relistSubmitting}
        error={relistError}
        onCancel={_onCloseRelist}
        onConfirm={_onConfirmRelist}
      />
      <ModalInfoCreditCard
        isOpen={modalCreditCard}
        toggle={() => setModalCreditCard(prev => !prev)}
        invoiceNumber={invoiceNumber}
      />
      <ModalBulkUpload
        isOpen={modalBulkUpload}
        toggle={() => setModalBulkUpload(prev => !prev)}
        bulkFormat={bulkAWBFormats.reduce((prev, current) => `${prev} & ${current}`)}
        onSubmit={_handleOnSubmitBulkUpload}
        isLoading={props.bulkUploadAWBIsSubmit}
        errorMessage={props.bulkUploadAWBErrorMessage}
        successMessage={props.bulkUploadAWBSuccess}
      />
      <Col xs={12}>
        <Card>
          <CardHeaderWithToolbars>
            <Title>
              <i className="fa fa-align-justify"></i>
              <span>{title}</span>
            </Title>
            {isDataLoaded && <Toolbars
              isOpen={filterOpen}
              onToggleFilter={_toggleFilterPanel}
              onRefreshList={_refreshList}
              onResetFilter={_handleResetFilter}
              isOpenPrint={printOpen}
              onTogglePrint={() => setPrintOpen(prev => !prev)}
            />}
          </CardHeaderWithToolbars>
          <FilterContainer>
            <FiltersPanel
              {...params}
              hideCreatedSourceFilter={Boolean(createdSource) && !showCreatedSourceFilter}
              createdSourceOptions={createdSourceOptions}
              onApplyFilter={_handleApplyFilter}
              isReset={isResetFilter}
              onResetFilterCallback={() => { setIsResetFilter(false); setFilterOpen(false); }}
              isOpen={filterOpen}
            />
          </FilterContainer>
          <CardBody>
            <CardText>
              In this section we have to set ready to deliver the order after legit check passed.
            </CardText>
            <div className="table-responsive">
              <PrintSelectionBar
                data={rows}
                selectedData={selectedData}
                setSelectedData={setSelectedData}
                isOpenPrint={printOpen}
                doPrint={(saleIds) => props.printLabels(saleIds)}
                onPrinted={onPrinted}
              />
              <Tbl>
                <TblHead>
                  <RowHeader
                    categoryName={params.category}
                    categoryValue={params.category}
                    onCategoryChanged={_handleCategoryChanged}
                  />
                </TblHead>
                <TblBody
                  isLoad={props.sale.isFetch && !props.sale.isLoadingMore}
                  hasRow={props.sale.list.status_code === 200 && rows.length > 0}
                  columnCount={7}
                >
                  {_onRenderRowItem()}
                </TblBody>
              </Tbl>
            </div>
            {_onRenderPagination()}
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

const mapStateToProps = (state) => {
  const { sale, auth: { isFetch, isLogged, roles, id, email } } = state;
  return {
    sale,
    email,
    guardData: { isFetch, isLogged, roles, id, email },
    bulkUploadAWBIsSubmit: bulkUploadAWBIsSubmitSelector(state),
    bulkUploadAWBErrorMessage: bulkUploadAWBErrorMessageSelector(state),
    bulkUploadAWBSuccess: bulkUploadAWBSuccessSelector(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  getList: (query) => dispatch(getSaleList(query)),
  deliverSneaker: (id, payload) => dispatch(doDeliverSneakerSale(id, payload)),
  doBulkAWB: (payload) => dispatch(doBulkAWB(payload)),
  printLabels: (saleIds) => dispatch(doPrintSaleLabels(saleIds)),
  confirmMarketplacePickup: (id) => dispatch(doConfirmMarketplacePickup(id)),
  restoreSellerStock: (id) => dispatch(submitSaleReturn(id, 'restore-seller-stock', {})),
});

const enhance = connect(mapStateToProps, mapDispatchToProps);

export default enhance(privateView(PreDeliveringContainer, ['supervisor', 'sale_supervisor', 'cashier']));
